import { hueFor, initials } from './avatar';
import type { ContactGroupCardData } from './types';

/**
 * ONE card holding contacts SEGMENTED by a shared field (company / title /
 * relationship / location). Each group renders a header row ("ACME · 3") with
 * its members listed beneath — so the grouping is visible, unlike the flat
 * ContactsGridCard. Each member row is clickable → onOpenContact(id), opening
 * the existing ContactCardPopover. Emitted by the agent's `group_contacts` tool.
 */

const GROUP_BY_LABEL: Record<ContactGroupCardData['groupBy'], string> = {
  company: 'company',
  title: 'title',
  relationship: 'relationship',
  location: 'location',
};

export function ContactGroupCard({
  data,
  onOpenContact,
}: {
  data: ContactGroupCardData;
  onOpenContact: (id: string) => void;
}) {
  const groups = (data.groups ?? []).filter((g) => g.contacts?.length);
  if (!groups.length) return null;
  const total = groups.reduce((n, g) => n + g.contacts.length, 0);

  return (
    <div className="rc-card rc-contacts rc-contact-groups">
      <div className="rc-meta">
        <span className="rc-meta-kind">👤 GROUPED BY {GROUP_BY_LABEL[data.groupBy].toUpperCase()}</span>
        <span className="rc-meta-sep">·</span>
        <span className="rc-meta-match">{total}</span>
      </div>

      {groups.map((group, gi) => (
        // Key on index too: a real field value equal to the "No <field>" missing
        // label would otherwise collide with the missing bucket's key. (#code-review)
        <div className="rc-contact-group" key={`${gi}-${group.label}`}>
          <div className="rc-contact-group-head">
            <span className="rc-contact-group-label">{group.label}</span>
            <span className="rc-contact-group-count">{group.count}</span>
          </div>
          <div className="rc-contacts-grid">
            {group.contacts.map((c) => {
              const sub = c.email || c.title || c.company || '';
              return (
                <button
                  key={c.id}
                  type="button"
                  className="rc-contact-cell"
                  onClick={() => onOpenContact(c.id)}
                >
                  {c.photoUrl ? (
                    <img className="rc-av" src={c.photoUrl} alt="" />
                  ) : (
                    <div
                      className="rc-av rc-av-initials"
                      style={{ background: hueFor(c.email || c.name || c.id) }}
                    >
                      {initials(c.name, c.email || '')}
                    </div>
                  )}
                  <div className="rc-contact-text">
                    <span className="rc-contact-name">{c.name || c.email || 'Unknown'}</span>
                    {sub && <span className="rc-contact-sub">{sub}</span>}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
